import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Input, Select } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import ImageUploader from '../../components/ImageUploader';
import VideoUploader from '../../components/VideoUploader';
import CityPicker from '../../components/CityPicker';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';

const PROPERTY_TYPES = ['flat', 'house', 'plot', 'shop', 'office', 'pg', 'warehouse', 'farmland'];
const FURNISHING = ['unfurnished', 'semi-furnished', 'furnished'];
const AMENITIES = ['Parking', 'Lift', 'Power backup', 'Water supply', 'Security', 'Park facing'];

/** Post a property or an animal. Same engine, different middle section. */
const PostListingPage = ({ kind }) => {
  const isProperty = kind === 'property';
  /* A used fridge is neither a flat nor a buffalo: its own short section. */
  const isItem = kind === 'item';
  const navigate = useNavigate();
  const toast = useToast();
  const { location } = useApp();

  const [f, setF] = useState({
    dealType: isProperty ? 'rent' : 'sale', title: '', description: '', price: '', deposit: '',
    images: [], video: '', city: '', district: '', state: '', address: '', negotiable: true,
    propertyType: 'flat', bedrooms: '', bathrooms: '', areaValue: '', furnishing: 'unfurnished', amenities: [],
    animalType: 'cow', animal: {},
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));


  // Which animals, and what to ask about each, come from the server so a new
  // species or question needs no release.
  const [species, setSpecies] = useState({});
  useEffect(() => {
    if (isProperty || isItem) return;
    api.listings.animal.schema().then((r) => setSpecies(r.data || {})).catch(() => {});
  }, [isProperty, isItem]);
  const spec = species[f.animalType] || species.other || { fields: [] };
  const setFact = (key, value) => setF((prev) => ({ ...prev, animal: { ...prev.animal, [key]: value } }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.title.trim().length < 4) return toast.error('Give it a clear title');
    if (!f.price) return toast.error('Enter a price');
    if (!f.city) return toast.error('Choose the city');

    setSaving(true);
    try {
      const body = {
        dealType: f.dealType, title: f.title.trim(), description: f.description, images: f.images, video: f.video,
        price: Number(f.price),
        priceUnit: isProperty && f.dealType === 'rent' ? 'per month' : 'total',
        deposit: Number(f.deposit) || 0, negotiable: f.negotiable,
        city: f.city, district: f.district, state: f.state, address: f.address,
        coordinates: location && location.longitude ? [location.longitude, location.latitude] : undefined,
        ...(isItem ? {
          item: {
            condition: f.condition || 'good',
            brand: f.brand || '',
            ageMonths: Number(f.ageMonths) || 0,
            hasBill: Boolean(f.hasBill),
            reasonForSelling: f.reasonForSelling || '',
          },
        } : {}),
        ...(isItem ? {} : isProperty
          ? { property: {
            propertyType: f.propertyType, bedrooms: Number(f.bedrooms) || 0, bathrooms: Number(f.bathrooms) || 0,
            areaValue: Number(f.areaValue) || 0, furnishing: f.furnishing, amenities: f.amenities,
          } }
          : { animal: {
            animalType: f.animalType,
            ...Object.fromEntries(spec.fields.map((fl) => {
              const v = f.animal[fl.key];
              if (fl.kind === 'number') return [fl.key, Number(v) || 0];
              if (fl.kind === 'bool') return [fl.key, Boolean(v)];
              return [fl.key, v || ''];
            })),
          } }),
      };
      const res = await api.listings[kind].create(body);
      toast.success(res.message, 'Submitted');
      navigate(`/${isProperty ? 'property' : isItem ? 'buy-sell' : 'animals'}`);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader
        title={isProperty ? 'Post a property' : isItem ? 'Sell something' : 'Sell an animal'}
        subtitle="Goes live once our team approves it"
        back
      />
      <Reveal className="card">
        <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
          {isItem ? (
            <>
              <div>
                <div className="t-small" style={{ marginBottom: 6, fontWeight: 700 }}>What condition is it in?</div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {['new', 'like new', 'good', 'fair', 'for parts'].map((c) => (
                    <Chip
                      key={c}
                      label={c}
                      active={(f.condition || 'good') === c}
                      onClick={() => set({ condition: c })}
                    />
                  ))}
                </div>
              </div>
              <Input label="Make or brand (optional)" value={f.brand || ''} onChange={(e) => set({ brand: e.target.value })} placeholder="Samsung, Hero, Prestige…" />
              <Input label="Roughly how old, in months (optional)" value={f.ageMonths || ''} inputMode="numeric" onChange={(e) => set({ ageMonths: e.target.value.replace(/\D/g, '') })} />
              <Input
                label="Why are you selling it? (optional)"
                value={f.reasonForSelling || ''}
                onChange={(e) => set({ reasonForSelling: e.target.value })}
                placeholder="Moving city, upgraded, not needed"
                hint="Buyers of used things nearly always ask this first."
              />
              <Chip
                label={f.hasBill ? '🧾 I have the bill' : '🧾 No bill'}
                active={Boolean(f.hasBill)}
                onClick={() => set({ hasBill: !f.hasBill })}
              />
            </>
          ) : null}

          {isProperty ? (
            <div>
              <div className="t-small" style={{ marginBottom: 6, fontWeight: 700 }}>Rent or sell?</div>
              <div className="row" style={{ gap: 8 }}>
                {['rent', 'sale'].map((d) => (
                  <Chip key={d} label={d === 'rent' ? '🔑 For rent' : '🏷️ For sale'} active={f.dealType === d} onClick={() => set({ dealType: d })} />
                ))}
              </div>
            </div>
          ) : null}

          {!isItem ? (
            <div>
              <div className="t-small" style={{ marginBottom: 6, fontWeight: 700 }}>{isProperty ? 'Property type' : 'Which animal?'}</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {isProperty
                  ? PROPERTY_TYPES.map((t) => (
                    <Chip key={t} label={t} active={f.propertyType === t} onClick={() => set({ propertyType: t })} />
                  ))
                  : Object.entries(species).map(([value, sp]) => (
                    <Chip key={value} label={`${sp.emoji} ${sp.label}`} active={f.animalType === value}
                      onClick={() => set({ animalType: value, animal: {} })} />
                  ))}
              </div>
            </div>
          ) : null}

          <Input label="Title" value={f.title} onChange={(e) => set({ title: e.target.value })}
            placeholder={isProperty
              ? 'e.g. 2 BHK flat in Pitampura'
              : isItem
                ? 'e.g. Samsung double door fridge, 2 years old'
                : 'e.g. Murrah buffalo, 4 years · Labrador puppy, 3 months'} />

          <div className="form-row">
            <Input className="grow" inputMode="numeric"
              label={isProperty && f.dealType === 'rent' ? 'Rent per month (₹)' : 'Price (₹)'}
              value={f.price} onChange={(e) => set({ price: e.target.value.replace(/\D/g, '') })} />
            {isProperty && f.dealType === 'rent' ? (
              <Input className="grow" inputMode="numeric" label="Deposit (₹)" value={f.deposit}
                onChange={(e) => set({ deposit: e.target.value.replace(/\D/g, '') })} />
            ) : null}
          </div>

          {isProperty ? (
            <>
              <div className="form-row">
                <Input className="grow" inputMode="numeric" label="Bedrooms" value={f.bedrooms} onChange={(e) => set({ bedrooms: e.target.value.replace(/\D/g, '') })} />
                <Input className="grow" inputMode="numeric" label="Bathrooms" value={f.bathrooms} onChange={(e) => set({ bathrooms: e.target.value.replace(/\D/g, '') })} />
                <Input className="grow" inputMode="numeric" label="Area (sq ft)" value={f.areaValue} onChange={(e) => set({ areaValue: e.target.value.replace(/\D/g, '') })} />
              </div>
              <Select label="Furnishing" value={f.furnishing} onChange={(e) => set({ furnishing: e.target.value })}
                options={FURNISHING.map((x) => ({ value: x, label: x }))} />
              <div>
                <div className="t-small" style={{ marginBottom: 6, fontWeight: 700 }}>Amenities</div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {AMENITIES.map((a) => (
                    <Chip key={a} label={a} active={f.amenities.includes(a)}
                      onClick={() => set({ amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] })} />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Rendered from the species spec - a cow asks about calvings, a bird about being hand-tamed. */}
              <div className="form-row" style={{ flexWrap: 'wrap' }}>
                {spec.fields.filter((fl) => fl.kind !== 'bool').map((fl) => (fl.kind === 'select' ? (
                  <Select
                    key={fl.key}
                    className="grow"
                    label={fl.label}
                    value={f.animal[fl.key] || ''}
                    onChange={(e) => setFact(fl.key, e.target.value)}
                    options={[{ value: '', label: '—' }, ...fl.options.map((o) => ({ value: o, label: o[0].toUpperCase() + o.slice(1) }))]}
                  />
                ) : (
                  <Input
                    key={fl.key}
                    className="grow"
                    inputMode={fl.kind === 'number' ? 'numeric' : undefined}
                    label={fl.unit ? `${fl.label} (${fl.unit})` : fl.label}
                    placeholder={fl.placeholder}
                    value={f.animal[fl.key] ?? ''}
                    onChange={(e) => setFact(fl.key, fl.kind === 'number' ? e.target.value.replace(/\D/g, '') : e.target.value)}
                  />
                )))}
              </div>
              <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
                {spec.fields.filter((fl) => fl.kind === 'bool').map((fl) => (
                  <label key={fl.key} className="switch">
                    <input type="checkbox" checked={Boolean(f.animal[fl.key])} onChange={(e) => setFact(fl.key, e.target.checked)} /> {fl.label}
                  </label>
                ))}
              </div>
            </>
          )}

          <ImageUploader label="Photos" folder="misc" multiple value={f.images} onChange={(v) => set({ images: v })} />

          <VideoUploader
            label={isProperty ? 'Walk-through video (optional)' : 'Video (optional)'}
            hint={isProperty ? 'A slow walk through each room sells a flat better than eight photos.' : 'Show the animal walking and feeding.'}
            folder="listing"
            value={f.video}
            onChange={(v) => set({ video: v })}
          />

          <div className="field">
            <label>Description</label>
            <textarea className="textarea" rows={4} value={f.description} onChange={(e) => set({ description: e.target.value })}
              placeholder={isProperty ? 'Floor, facing, nearby market, metro…' : 'Health, feeding, temperament, why you are selling…'} />
          </div>

          <CityPicker label="City" value={f} onChange={({ city, district, state }) => set({ city, district, state })} />
          <Input label="Area / address (optional)" value={f.address} onChange={(e) => set({ address: e.target.value })} />
          <label className="switch"><input type="checkbox" checked={f.negotiable} onChange={(e) => set({ negotiable: e.target.checked })} /> Price negotiable</label>

          <Button title="Submit for approval" icon="⚡" type="submit" size="lg" full loading={saving} />
        </form>
      </Reveal>
    </>
  );
};

export default PostListingPage;
